import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Table, Button, Modal, Form, Input, InputNumber, Tag, message, Space } from "antd";
import { ArrowLeft, MapPin, RefreshCw } from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useEmployeeLocations, useUpsertEmployeeLocation } from "../../../api-hooks/jobs";

dayjs.extend(relativeTime);

export default function EmployeeLocationsPage() {
  const { companyId } = useParams<{ companyId: string }>();
  const cid = Number(companyId);
  const navigate = useNavigate();
  const [msgApi, ctx] = message.useMessage();

  const { data: locations = [], isLoading, refetch } = useEmployeeLocations(cid);

  const [modal, setModal] = useState(false);
  const [form] = Form.useForm();

  const upsert = useUpsertEmployeeLocation(cid, {
    onSuccess: () => { msgApi.success("Location updated."); setModal(false); form.resetFields(); refetch(); },
    onError: (e: any) => msgApi.error(e?.message || "Error"),
  });

  function handleSubmit(vals: any) {
    upsert.mutate({ ...vals, company_id: cid });
  }

  const columns = [
    {
      title: "Employee",
      key: "emp",
      render: (_: any, r: any) => (
        <div>
          <div className="font-medium text-sm text-slate-800">{r.user_name?.trim() || "—"}</div>
          <div className="text-xs text-slate-400">{r.user_email}</div>
          {r.user_job_title && <div className="text-xs text-slate-400">{r.user_job_title}</div>}
        </div>
      ),
    },
    {
      title: "Location",
      key: "loc",
      render: (_: any, r: any) => (
        <div>
          <div className="text-sm text-slate-700">{r.address || `${r.latitude}, ${r.longitude}`}</div>
          <div className="text-xs text-slate-400">
            {r.latitude}, {r.longitude}
            {r.accuracy ? ` (±${Number(r.accuracy).toFixed(0)}m)` : ""}
          </div>
        </div>
      ),
    },
    {
      title: "Last Seen",
      dataIndex: "recorded_at",
      key: "ts",
      render: (v: string) => (
        <div>
          <div className="text-sm text-slate-700">{dayjs(v).fromNow()}</div>
          <div className="text-xs text-slate-400">{dayjs(v).format("DD/MM/YYYY HH:mm")}</div>
        </div>
      ),
    },
    {
      title: "Map",
      key: "map",
      render: (_: any, r: any) => (
        <a
          href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`}
          target="_blank"
          rel="noreferrer"
          className="text-violet-600 text-sm hover:underline"
        >
          Open Maps
        </a>
      ),
    },
  ];

  return (
    <div>
      {ctx}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-violet-600 hover:text-violet-800 flex items-center gap-1 text-sm">
            <ArrowLeft size={16} /> Back
          </button>
          <div>
            <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
              <MapPin size={20} className="text-violet-600" />
              Employee Locations — Company #{cid}
            </h1>
            <p className="text-slate-500 text-xs">{locations.length} employee{locations.length !== 1 ? "s" : ""} with location data</p>
          </div>
        </div>
        <Space>
          <Button icon={<RefreshCw size={14} />} onClick={() => refetch()}>Refresh</Button>
          <Button
            type="primary"
            icon={<MapPin size={16} />}
            onClick={() => { form.resetFields(); setModal(true); }}
            style={{ background: "#4c1d95", borderColor: "#4c1d95" }}
          >
            Update Location
          </Button>
        </Space>
      </div>

      {locations.length === 0 && !isLoading && (
        <div className="bg-violet-50 border border-violet-200 rounded-xl p-6 text-center mb-4">
          <MapPin size={28} className="text-violet-400 mx-auto mb-2" />
          <div className="text-slate-600 font-medium">No location data yet</div>
          <div className="text-slate-400 text-sm mt-1">
            Location data is sent by the employee mobile app, or you can manually update it below.
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <Table
          rowKey="id"
          dataSource={locations}
          columns={columns}
          loading={isLoading}
          size="middle"
          pagination={false}
        />
      </div>

      <Modal
        open={modal}
        onCancel={() => { setModal(false); form.resetFields(); }}
        title="Update Employee Location"
        onOk={() => form.submit()}
        okButtonProps={{ style: { background: "#4c1d95", borderColor: "#4c1d95" }, loading: upsert.isPending }}
        okText="Save Location"
        destroyOnClose
      >
        <Form form={form} layout="vertical" size="middle" onFinish={handleSubmit} className="pt-2">
          <Form.Item name="user_id" label="User ID" rules={[{ required: true, message: "User ID required" }]}>
            <InputNumber className="w-full" placeholder="Enter user ID" />
          </Form.Item>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="latitude" label="Latitude" rules={[{ required: true, message: "Required" }]}>
              <InputNumber className="w-full" placeholder="51.5074" step={0.0001} />
            </Form.Item>
            <Form.Item name="longitude" label="Longitude" rules={[{ required: true, message: "Required" }]}>
              <InputNumber className="w-full" placeholder="-0.1278" step={0.0001} />
            </Form.Item>
          </div>
          <Form.Item name="accuracy" label="Accuracy (meters)">
            <InputNumber className="w-full" placeholder="Optional" min={0} />
          </Form.Item>
          <Form.Item name="address" label="Address">
            <Input placeholder="Optional readable address" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
